import { AppConfig, ServerMap, ServerUrls } from '@frontend/core'
import { generateEmptyServerState } from '@frontend/core/lib/modules/servers/slice'

export function generateServerUrls(
  serverMap: ServerMap,
  envVars: Record<string, string>,
  appConfig: AppConfig
): {
  [key in keyof typeof serverMap]: ServerUrls
} {
  const urls = { ...generateEmptyServerState().urls }
  const metricsServerIp =
    localStorage.getItem('METRICS_SERVER_IP') ?? 'localhost'

  urls.metricsServer = {
    url: `http://${metricsServerIp}:8888`,
    websocketUrl: `ws://${metricsServerIp}:8888/ws/`
  }

  urls.staticAssetApi = {
    url: `http://${metricsServerIp}:8888`,
    websocketUrl: `ws://${metricsServerIp}:8888/ws/`
  }

  Object.keys(serverMap).forEach((key) => {
    const serverKey = key as keyof typeof serverMap

    // If the app is pitch side connect to the match tracker API via the metrics server //
    if (appConfig.pitchSide) {
      for (const key in urls) {
        let url = urls.metricsServer.url
        if (key === 'matchTrackerApi') url = urls.metricsServer.url + '/api/v2/'

        urls[key as keyof typeof urls] = {
          url,
          websocketUrl: urls.metricsServer.websocketUrl
        }
      }
    }

    if (
      'urlKey' in serverMap[serverKey] &&
      typeof serverMap[serverKey].urlKey === 'string'
    ) {
      const serverUrlKey = serverMap[serverKey].urlKey
      if (!envVars[serverUrlKey]) return
      const serverUrl = envVars[serverUrlKey]

      // Check if the URL is a valid sportable URL //
      if (isSportableCloudUrl(serverUrl)) {
        const finalServerUrl = cloudURL(serverUrl)
        const finalWebsocketUrl = cloudWSURL(serverUrl)
        urls[serverKey] = {
          url: finalServerUrl + '/',
          websocketUrl: finalWebsocketUrl
        }
      } else {
        urls[serverKey] = {
          url: localURL(serverUrl),
          websocketUrl: localWSURL(serverUrl) + '/ws'
        }
      }
    }
  })

  // If the app is pitch side connect to the match tracker API via the metrics server //
  if (appConfig.pitchSide) {
    urls.matchTrackerApi = {
      url: urls.metricsServer.url + '/api/v2/',
      websocketUrl: urls.metricsServer.websocketUrl
    }

    urls.staticAssetApi = {
      url: urls.metricsServer.url + '/api/v2',
      websocketUrl: urls.metricsServer.websocketUrl
    }
  }

  return urls
}

function cloudURL(url: string): string {
  const converted = url
    .replace('http://', 'https://')
    .replace('ws://', 'wss://')
  // If URL doesn't have a protocol, add https://
  if (!converted.includes('://')) {
    return 'https://' + converted
  }
  return converted
}

function cloudWSURL(url: string): string {
  const converted = url
    .replace('http://', 'wss://')
    .replace('ws://', 'wss://')
    .replace('https://', 'wss://')
  // If URL doesn't have a protocol, add wss://
  if (!converted.includes('://')) {
    return 'wss://' + converted
  }
  return converted
}

function localURL(url: string): string {
  if (url.startsWith('http://')) {
    return url
  }
  if (url.startsWith('https://')) {
    return url.replace('https://', 'http://')
  }
  // Don't prepend http:// to ws:// or wss:// URLs
  if (url.startsWith('ws://') || url.startsWith('wss://')) {
    return url.replace('wss://', 'ws://').replace('https://', 'http://')
  }
  return 'http://' + url
}

function localWSURL(url: string): string {
  if (url.startsWith('http://')) {
    return url.replace('http://', 'ws://')
  }
  if (url.startsWith('https://')) {
    return url.replace('https://', 'ws://')
  }
  if (url.startsWith('ws://')) {
    return url
  }
  if (url.startsWith('wss://')) {
    return url.replace('wss://', 'ws://')
  }
  return 'ws://' + url
}

function isSportableCloudUrl(url: string): boolean {
  try {
    // Handle ws:// and wss:// protocols by converting to http:// for parsing
    let urlToParse = url
    if (url.startsWith('ws://')) {
      urlToParse = url.replace('ws://', 'http://')
    } else if (url.startsWith('wss://')) {
      urlToParse = url.replace('wss://', 'https://')
    } else if (!url.startsWith('http')) {
      urlToParse = `https://${url}`
    }
    const parsedUrl = new URL(urlToParse)
    return parsedUrl.hostname.endsWith('.sportable.com')
  } catch (e) {
    // Invalid URL
    console.error('Invalid URL:', e)
    return false
  }
}
